import React, { useEffect, useState } from "react";
import { Skeleton } from "antd";
import SectionHeader from "./SectionHeader";
import ScrollCarousel from "./ScrollCarousel";
import gdBrandsApi, { GdBrand } from "../../../../api-services/gd-brands-api.service";

type HoverPlacement = "left" | "right";

function brandHref(brand: GdBrand) {
  return brand.redirectionUrl || "/web";
}

const HOVER_CARD_WIDTH = 430;
const HOVER_VIEWPORT_GAP = 24;

const COLLECTION_BACKGROUND_FALLBACKS: Array<[RegExp, string]> = [
  [/signature/i, "/iceholidays-assets/images/about_signaturetours_cover.png"],
  [/in[- ]?depth/i, "/iceholidays-assets/images/gd_indepth_v2.png"],
  [/fs holiday/i, "/iceholidays-assets/images/limited_time_promo_3.png"],
  [/fly cruise/i, "/iceholidays-assets/images/gd_flight_cover_latest.png"],
  [/world cruise|gd cruise/i, "/iceholidays-assets/images/gdcruise_cover.png"],
  [/classic|standard/i, "/iceholidays-assets/images/gd_classics_v2.png"],
];

function brandBackground(brand: GdBrand) {
  if (brand.backgroundImage) return brand.backgroundImage;

  const matchedFallback = COLLECTION_BACKGROUND_FALLBACKS.find(([pattern]) => pattern.test(brand.title));
  return matchedFallback?.[1] || "/iceholidays-assets/images/v2-hero-bg.jpg";
}

function brandTypeLabel(brand: GdBrand) {
  return brand.brandType === "cruise" ? "Cruises" : "Tours";
}

function brandCountLabel(brand: GdBrand) {
  const type = brand.brandType === "cruise" ? "cruises" : "tours";
  const count = brand.tourCount || 0;
  return count > 0 ? `${count}+ ${type}` : `0 ${type}`;
}

// "Explore Our Travel Collections" — fixed-width (160px) brand/collection logo
// cards in a native scroll-snap track (clamps cleanly at both ends).
export default function TravelCollections() {
  const [brands, setBrands] = useState<GdBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoverPlacements, setHoverPlacements] = useState<Record<string, HoverPlacement>>({});

  useEffect(() => {
    let active = true;

    gdBrandsApi.getBrands()
      .then((data) => {
        if (active) setBrands(data);
      })
      .catch(() => {
        if (active) setBrands([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  if (!loading && brands.length === 0) return null;

  const skeletonCards = Array.from({ length: 6 }, (_, index) => (
    <div className="v2-collection-card" key={index}>
      <Skeleton.Image active />
      <Skeleton.Input active size="small" />
    </div>
  ));

  const updateHoverPlacement = (brandId: string, element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const trackRect = element.closest(".v2-carousel__track")?.getBoundingClientRect();
    const boundaryRight = trackRect?.right || window.innerWidth;
    const rightEdge = rect.right + HOVER_CARD_WIDTH;
    const placement = rightEdge > boundaryRight - HOVER_VIEWPORT_GAP ? "left" : "right";

    setHoverPlacements((current) => (
      current[brandId] === placement ? current : { ...current, [brandId]: placement }
    ));
  };

  return (
    <section className="v2-section" id="travel-collections-v2">
      <SectionHeader title="Explore Our Travel Collections" subtitle="Find holidays tailored to every travel style." viewAllHref="/web" />
      <ScrollCarousel trackClassName="v2-collections__track" step={352}>
        {loading ? skeletonCards : brands.map((brand) => (
          <a
            href={brandHref(brand)}
            className={`v2-collection-card v2-collection-card--hover-${hoverPlacements[brand.id] || "right"}`}
            key={brand.id}
            onMouseEnter={(event) => updateHoverPlacement(brand.id, event.currentTarget)}
            onFocus={(event) => updateHoverPlacement(brand.id, event.currentTarget)}
          >
            <div className="v2-collection-card__logo">
              {brand.logo ? <img src={brand.logo} alt={brand.title} /> : <span>{brand.title}</span>}
            </div>
            <span className="v2-collection-card__name">{brand.title}</span>
            <span className="v2-collection-card__hover" aria-hidden="true">
              <span className="v2-collection-card__hover-bg" style={{ backgroundImage: `url(${brandBackground(brand)})` }} />
              <span className="v2-collection-card__hover-panel">
                <span className="v2-collection-card__hover-head">
                  <span className="v2-collection-card__hover-logo">
                    {brand.logo ? <img src={brand.logo} alt="" /> : <span>{brand.title.slice(0, 1)}</span>}
                  </span>
                  <span>
                    <span className="v2-collection-card__hover-type">{brandTypeLabel(brand)}</span>
                    <strong className="v2-collection-card__hover-title">{brand.title}</strong>
                  </span>
                </span>
                <span className="v2-collection-card__hover-copy">
                  {brand.description || brand.subtitle || "Find holidays tailored to this travel collection."}
                </span>
                <span className="v2-collection-card__hover-footer">
                  <span className="v2-collection-card__hover-count">{brandCountLabel(brand)}</span>
                  <span className="v2-collection-card__hover-link">Explore</span>
                </span>
              </span>
            </span>
          </a>
        ))}
      </ScrollCarousel>
    </section>
  );
}
